import React, { useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import SimpleBar from 'simplebar-react';
import MetisMenu from 'metismenujs';
import withRouter from 'components/Common/withRouter';
import { withTranslation } from 'react-i18next';
import { SHOP_PATHS } from 'routes/routesConstants';
import { Link, useParams } from 'react-router-dom';
import { usePermissions } from 'context/PermissionProvider';
import { PERMISSIONS } from 'constants/permissions';

const SidebarContent = (props) => {
  const { hasPermission, hasAnyPermission } = usePermissions();
  const { shopId } = useParams();
  const ref = useRef();

  const activateParentDropdown = useCallback((item) => {
    item.classList.add('active');
    const parent = item.parentElement;
    const parent2El = parent.childNodes[1];

    if (parent2El && parent2El.id !== 'side-menu') {
      parent2El.classList.add('mm-show');
    }

    if (parent) {
      parent.classList.add('mm-active');
      const parent2 = parent.parentElement;

      if (parent2) {
        parent2.classList.add('mm-show'); // ul tag

        const parent3 = parent2.parentElement; // li tag

        if (parent3) {
          parent3.classList.add('mm-active'); // li
          parent3.childNodes[0].classList.add('mm-active'); // a
          const parent4 = parent3.parentElement; // ul
          if (parent4) {
            parent4.classList.add('mm-show'); // ul
            const parent5 = parent4.parentElement;
            if (parent5) {
              parent5.classList.add('mm-show'); // li
              parent5.childNodes[0].classList.add('mm-active'); // a tag
            }
          }
        }
      }
      scrollElement(item);
      return false;
    }
    scrollElement(item);
    return false;
  }, []);

  const removeActivation = (items) => {
    for (var i = 0; i < items.length; ++i) {
      var item = items[i];
      const parent = items[i].parentElement;

      if (item && item.classList.contains('active')) {
        item.classList.remove('active');
      }
      if (parent) {
        const parent2El =
          parent.childNodes && parent.childNodes.length && parent.childNodes[1]
            ? parent.childNodes[1]
            : null;
        if (parent2El && parent2El.id !== 'side-menu') {
          parent2El.classList.remove('mm-show');
        }

        parent.classList.remove('mm-active');
        const parent2 = parent.parentElement;

        if (parent2) {
          parent2.classList.remove('mm-show');

          const parent3 = parent2.parentElement;
          if (parent3) {
            parent3.classList.remove('mm-active'); // li
            parent3.childNodes[0].classList.remove('mm-active');

            const parent4 = parent3.parentElement; // ul
            if (parent4) {
              parent4.classList.remove('mm-show'); // ul
              const parent5 = parent4.parentElement;
              if (parent5) {
                parent5.classList.remove('mm-show'); // li
                parent5.childNodes[0].classList.remove('mm-active'); // a tag
              }
            }
          }
        }
      }
    }
  };

  const activeMenu = useCallback(() => {
    // no active logic here for now since no routing
  }, []);

  useEffect(() => {
    ref.current.recalculate();
  }, []);

  useEffect(() => {
    new MetisMenu('#side-menu');
    activeMenu();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    activeMenu();
  }, [activeMenu]);

  function scrollElement(item) {
    if (item) {
      const currentPosition = item.offsetTop;
      if (currentPosition > window.innerHeight) {
        ref.current.getScrollElement().scrollTop = currentPosition - 300;
      }
    }
  }

  return (
    <React.Fragment>
      <SimpleBar className="h-100" ref={ref}>
        <div id="sidebar-menu">
          <ul className="metismenu list-unstyled" id="side-menu">
            {hasPermission(PERMISSIONS.VIEW_DASHBOARD) && (
              <li>
                <Link to={SHOP_PATHS.MEDICINE_SELL_DB_VIEW.replace(':shopId', shopId)}>
                  <i className="bx bxs-dashboard"></i>
                  <span>{props.t('Dashboard')}</span>
                </Link>
              </li>
            )}

            {hasAnyPermission([PERMISSIONS.VIEW_INVENTORY, PERMISSIONS.CREATE_INVENTORY]) && (
              <>
                <li className="menu-title">{props.t('Inventory')}</li>
                <li>
                  <Link to="#" className="has-arrow">
                    <i className="bx bx-package"></i>
                    <span>{props.t('Inventory')}</span>
                  </Link>
                  <ul className="sub-menu">
                    {hasPermission(PERMISSIONS.VIEW_INVENTORY) && (
                      <li>
                        <Link to={SHOP_PATHS.INVENTORIES.replace(':shopId', shopId)}>
                          <i className="bx bx-list-ul"></i>
                          <span>{props.t('Inventory List')}</span>
                        </Link>
                      </li>
                    )}
                    {hasPermission(PERMISSIONS.CREATE_INVENTORY) && (
                      <li>
                        <Link to={SHOP_PATHS.ADD_INVENTORY.replace(':shopId', shopId)}>
                          <i className="bx bx-plus-circle"></i>
                          <span>{props.t('Add Inventory')}</span>
                        </Link>
                      </li>
                    )}
                  </ul>
                </li>
              </>
            )}

            {hasAnyPermission([PERMISSIONS.VIEW_SALES, PERMISSIONS.CREATE_SALES]) && (
              <>
                <li className="menu-title">{props.t('Sells')}</li>
                <li>
                  <Link to="#" className="has-arrow">
                    <i className="bx bx-store"></i>
                    <span>{props.t('Sells')}</span>
                  </Link>
                  <ul className="sub-menu">
                    {hasPermission(PERMISSIONS.VIEW_SALES) && (
                      <li>
                        <Link to={SHOP_PATHS.MEDICINE_SELL_INVOICES.replace(':shopId', shopId)}>
                          <i className="bx bx-list-ul"></i>
                          <span>{props.t('Sell List')}</span>
                        </Link>
                      </li>
                    )}
                    {hasPermission(PERMISSIONS.CREATE_SALES) && (
                      <li>
                        <Link to={SHOP_PATHS.MEDICINE_SELL_ENTRY.replace(':shopId', shopId)}>
                          <i className="bx bx-plus-circle"></i>
                          <span>{props.t('New Sell')}</span>
                        </Link>
                      </li>
                    )}
                  </ul>
                </li>
              </>
            )}

            {hasAnyPermission([PERMISSIONS.VIEW_PURCHASE, PERMISSIONS.CREATE_PURCHASE]) && (
              <>
                <li className="menu-title">{props.t('Purchases')}</li>
                <li>
                  <Link to="#" className="has-arrow">
                    <i className="bx bx-shopping-bag"></i>
                    <span>{props.t('Purchases')}</span>
                  </Link>
                  <ul className="sub-menu">
                    {hasPermission(PERMISSIONS.VIEW_PURCHASE) && (
                      <li>
                        <Link to={SHOP_PATHS.PURCHASE_INVOICES.replace(':shopId', shopId)}>
                          <i className="bx bx-list-ul"></i>
                          <span>{props.t('Purchase List')}</span>
                        </Link>
                      </li>
                    )}
                    {hasPermission(PERMISSIONS.CREATE_PURCHASE) && (
                      <li>
                        <Link to={SHOP_PATHS.PURCHASE_ENTRY.replace(':shopId', shopId)}>
                          <i className="bx bx-plus-circle"></i>
                          <span>{props.t('New Purchase')}</span>
                        </Link>
                      </li>
                    )}
                  </ul>
                </li>
              </>
            )}

            {hasAnyPermission([PERMISSIONS.VIEW_SHOP_ITEMS, PERMISSIONS.CREATE_SHOP_ITEMS]) && (
              <>
                <li className="menu-title">{props.t('Items')}</li>
                <li>
                  <Link to="#" className="has-arrow">
                    <i className="bx bx-list-ul"></i>
                    <span>{props.t('Items')}</span>
                  </Link>
                  <ul className="sub-menu">
                    {hasPermission(PERMISSIONS.VIEW_SHOP_ITEMS) && (
                      <li>
                        <Link to={SHOP_PATHS.ITEMS.replace(':shopId', shopId)}>
                          <i className="bx bx-list-ul"></i>
                          <span>{props.t('List Items')}</span>
                        </Link>
                      </li>
                    )}
                    {hasPermission(PERMISSIONS.CREATE_SHOP_ITEMS) && (
                      <li>
                        <Link to={SHOP_PATHS.ADD_ITEM.replace(':shopId', shopId)}>
                          <i className="bx bx-plus-circle"></i>
                          <span>{props.t('Add Item')}</span>
                        </Link>
                      </li>
                    )}
                  </ul>
                </li>
              </>
            )}

            {hasAnyPermission([PERMISSIONS.VIEW_UNITS, PERMISSIONS.MANAGE_UNITS]) && (
              <>
                <li className="menu-title">{props.t('Units')}</li>
                <li>
                  <Link to="#" className="has-arrow">
                    <i className="bx bx-shape-square"></i>
                    <span>{props.t('Units')}</span>
                  </Link>
                  <ul className="sub-menu">
                    {hasPermission(PERMISSIONS.VIEW_UNITS) && (
                      <li>
                        <Link to={SHOP_PATHS.ITEM_UNITS.replace(':shopId', shopId)}>
                          <i className="bx bx-list-ul"></i>
                          <span>{props.t('List Units')}</span>
                        </Link>
                      </li>
                    )}
                    {hasPermission(PERMISSIONS.MANAGE_UNITS) && (
                      <li>
                        <Link to={SHOP_PATHS.ADD_UNIT.replace(':shopId', shopId)}>
                          <i className="bx bx-plus-circle"></i>
                          <span>{props.t('Add Unit')}</span>
                        </Link>
                      </li>
                    )}
                  </ul>
                </li>
              </>
            )}

            {hasAnyPermission([
              PERMISSIONS.VIEW_SALES_ADJUSTMENTS,
              PERMISSIONS.VIEW_PURCHASE_ADJUSTMENTS,
            ]) && (
              <>
                <li className="menu-title">{props.t('Adjustments')}</li>
                <li>
                  <Link to="#" className="has-arrow">
                    <i className="bx bx-adjust"></i>
                    <span>{props.t('Adjustments')}</span>
                  </Link>
                  <ul className="sub-menu">
                    {hasPermission(PERMISSIONS.VIEW_SALES_ADJUSTMENTS) && (
                      <li>
                        <Link to={SHOP_PATHS.SELL_ADJUSTMENT_ITEMS.replace(':shopId', shopId)}>
                          <i className="bx bx-list-ul"></i>
                          <span>{props.t('Sell Adjustments')}</span>
                        </Link>
                      </li>
                    )}
                    {hasPermission(PERMISSIONS.VIEW_PURCHASE_ADJUSTMENTS) && (
                      <li>
                        <Link
                          to={SHOP_PATHS.PURCHASE_ADJUSTMENT_ITEMS.replace(':shopId', shopId)}
                        >
                          <i className="bx bx-plus-circle"></i>
                          <span>{props.t('Purchase Adjustments')}</span>
                        </Link>
                      </li>
                    )}
                  </ul>
                </li>
              </>
            )}

            {hasPermission(PERMISSIONS.VIEW_LEDGER) && (
              <>
                <li className="menu-title">{props.t('Ledger')}</li>
                <li>
                  <Link to={SHOP_PATHS.LEDGER_ENTRIES.replace(':shopId', shopId)}>
                    <i className="bx bx-book"></i>
                    <span>{props.t('Ledger Entries')}</span>
                  </Link>
                </li>
              </>
            )}

            {hasPermission(PERMISSIONS.VIEW_STOCK_MOVEMENTS) && (
              <>
                <li className="menu-title">{props.t('Stock')}</li>
                <li>
                  <Link to={SHOP_PATHS.STOCK_MOVEMENTS.replace(':shopId', shopId)}>
                    <i className="bx bx-transfer-alt"></i>
                    <span>{props.t('Stock Movements')}</span>
                  </Link>
                </li>
              </>
            )}

            {hasAnyPermission([PERMISSIONS.EDIT_SHOP_SETTINGS, PERMISSIONS.MANAGE_SHOPS]) && (
              <>
                <li className="menu-title">{props.t('Shop Settings')}</li>
                <li>
                  <Link to={SHOP_PATHS.EDIT.replace(':shopId', shopId)}>
                    <i className="bx bx-cog"></i>
                    <span>{props.t('Settings')}</span>
                  </Link>
                </li>
              </>
            )}

            {/* <li className="menu-title">{props.t('Shop Details')}</li>

            <li>
              <a href="#">
                <i className="bx bx-store"></i>
                <span>{props.t('Shop Information')}</span>
              </a>
            </li>

            <li>
              <a href="#">
                <i className="bx bx-phone"></i>
                <span>{props.t('Contact Details')}</span>
              </a>
            </li>

            <li>
              <a href="#">
                <i className="bx bx-share-alt"></i>
                <span>{props.t('Social Profiles')}</span>
              </a>
            </li>

            <li>
              <a href="#">
                <i className="bx bx-time"></i>
                <span>{props.t('Business Hours')}</span>
              </a>
            </li>

            <li>
              <a href="#">
                <i className="bx bx-image"></i>
                <span>{props.t('Media')}</span>
              </a>
            </li>

            <li>
              <a href="#">
                <i className="bx bx-bar-chart-alt-2"></i>
                <span>{props.t('SEO Settings')}</span>
              </a>
            </li>

            <li>
              <a href="#">
                <i className="bx bx-cog"></i>
                <span>{props.t('Shop Settings')}</span>
              </a>
            </li>

            <li>
              <a href="#">
                <i className="bx bx-history"></i>
                <span>{props.t('Audit Log')}</span>
              </a>
            </li> */}

            {/* <li className="menu-title">{props.t('Users & Settings')}</li>

            <li>
              <a href="#">
                <i className="bx bx-user-circle"></i>
                <span>{props.t('Users & Permissions')}</span>
              </a>
            </li>

            <li>
              <a href="#">
                <i className="bx bx-sliders"></i>
                <span>{props.t('Settings')}</span>
              </a>
            </li>

            <li className="menu-title">{props.t('Support')}</li>

            <li>
              <a href="#">
                <i className="bx bx-help-circle"></i>
                <span>{props.t('FAQ')}</span>
              </a>
            </li>

            <li>
              <a href="#">
                <i className="bx bx-headphone"></i>
                <span>{props.t('Contact Support')}</span>
              </a>
            </li>

            <li>
              <a href="#">
                <i className="bx bx-book"></i>
                <span>{props.t('Documentation')}</span>
              </a>
            </li> */}
          </ul>
        </div>
      </SimpleBar>
    </React.Fragment>
  );
};

SidebarContent.propTypes = {
  t: PropTypes.any,
};

export default withRouter(withTranslation()(SidebarContent));
